import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'white'
type Size = 'md' | 'lg' | 'xl'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary/90',
  white: 'bg-white text-foreground shadow-sm hover:bg-white/80',
}

/* md = inline promos, lg = bands and funnel primaries, xl = hero scale. */
const SIZES: Record<Size, string> = {
  md: 'h-11 px-6',
  lg: 'h-12 px-7',
  xl: 'h-14 px-8',
}

/**
 * The storefront call-to-action: solid pill, bold label, optional trailing
 * arrow. One style family across hero, bands, promos and the checkout funnel;
 * `fullWidth` switches to the bar anatomy (label left, arrow right). Portal
 * and admin screens keep the tinted ui/Button - this is marketplace-only.
 */
export default function Cta({
  to,
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  size = 'lg',
  arrow = false,
  fullWidth = false,
  className,
  children,
}: {
  to?: string
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
  variant?: Variant
  size?: Size
  arrow?: boolean
  fullWidth?: boolean
  className?: string
  children: ReactNode
}) {
  const trailing = loading ? (
    <Loader2 className="size-4 animate-spin" />
  ) : arrow ? (
    <ArrowRight className="size-4" />
  ) : null

  const classes = cn(
    'inline-flex items-center rounded-full text-sm font-semibold transition-colors',
    fullWidth && trailing ? 'w-full justify-between' : fullWidth ? 'w-full justify-center gap-2' : 'justify-center gap-2',
    VARIANTS[variant],
    SIZES[size],
    className,
  )

  if (to && !disabled) {
    // Anything with a scheme leaves the site (admin-managed hero URLs).
    if (/^https?:\/\//i.test(to)) {
      return (
        <a href={to} target="_blank" rel="noopener noreferrer" className={classes}>
          {children}
          {trailing}
        </a>
      )
    }
    return (
      <Link to={to} className={classes}>
        {children}
        {trailing}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(classes, 'disabled:cursor-not-allowed disabled:opacity-50')}
    >
      {children}
      {trailing}
    </button>
  )
}
